import { useState } from 'react';
import { useConversations } from '../whatsapp/useConversations';
import { useWhatsapp } from '../store/whatsapp';
import { WhatsappChatPanel } from '../whatsapp/WhatsappChatPanel';
import { relativeTime } from '../lib/utils';
import type { WhatsappConversationStatus } from '@marnic/shared';

const STATUS_TABS: WhatsappConversationStatus[] = ['open', 'snoozed', 'closed'];

export function WhatsApp(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<WhatsappConversationStatus>('open');

  const { data, isLoading } = useConversations({ status: statusFilter });
  const items = data?.items ?? [];

  const activeId = useWhatsapp((s) => s.activeConversationId);
  const setActive = useWhatsapp((s) => s.setActiveConversation);

  const selected = items.find((c) => c.id === activeId) ?? null;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">WhatsApp</h1>
        <div className="flex gap-2">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
        {/* Left panel — conversation list */}
        <div className="card overflow-hidden p-0">
          {isLoading && (
            <div className="px-4 py-8 text-center text-sm text-ink-400">Loading…</div>
          )}
          {!isLoading && items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-ink-400">
              No {statusFilter} conversations.
            </div>
          )}
          <ul className="divide-y divide-ink-100">
            {items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setActive(c.id === activeId ? null : c.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-ink-50 transition-colors ${
                    c.id === activeId ? 'bg-brand-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-sm truncate">
                      {c.lead?.firstName} {c.lead?.lastName}
                      {c.lead?.company ? (
                        <span className="text-ink-500 font-normal"> · {c.lead.company}</span>
                      ) : null}
                    </span>
                    <span className="text-xs text-ink-400 shrink-0 mt-0.5">
                      {c.lastMessageAt ? relativeTime(c.lastMessageAt) : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-ink-500 font-mono">
                      {c.lead?.phoneE164 ?? '—'}
                    </span>
                    {c.unreadCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-brand-600 text-white text-xs font-semibold">
                        {c.unreadCount > 99 ? '99+' : c.unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Right panel — chat */}
        <div>
          <WhatsappChatPanel conversation={selected} />
        </div>
      </div>
    </div>
  );
}
